import { isDefined } from "@clipboard-health/util-ts";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { calculateTimesheetWorkedHours } from "./timesheetFormatUtils";

interface GetTimesheetTotalsBreakdownParams {
  displayTimesheet?: TimesheetEntry;
  verifiedTimesheet?: TimesheetEntry;
  hourlyPayRate: number;
  /** Server-computed instant pay amount. Preferred over a client-side estimate when defined. */
  instantPayTotalAmount?: number;
}

interface TimesheetTotalsBreakdown {
  paidOutAmountDisplay?: string;
  pendingPaymentAmountDisplay?: string;
}

/**
 * Derives the "Paid out via InstantPay" / "Pending payment" footer values when the
 * worker has a pending adjustment (verified hours < adjusted hours). Each row is
 * gated independently so the footer only shows meaningful values:
 * - `paidOutAmountDisplay` is only set when `instantPayTotalAmount` is defined.
 *   No synthesized fallback — a missing server amount means the row stays hidden.
 * - `pendingPaymentAmountDisplay` is only set when the computed pending amount is
 *   greater than zero (matching how the "Pending approval" hours row hides at 0).
 */
export function getTimesheetTotalsBreakdown(
  params: GetTimesheetTotalsBreakdownParams
): TimesheetTotalsBreakdown {
  const { displayTimesheet, verifiedTimesheet, hourlyPayRate, instantPayTotalAmount } = params;

  const verifiedHours = isDefined(verifiedTimesheet)
    ? calculateTimesheetWorkedHours(verifiedTimesheet)
    : undefined;
  const adjustedHours = isDefined(displayTimesheet)
    ? calculateTimesheetWorkedHours(displayTimesheet)
    : undefined;
  const pendingHours =
    isDefined(verifiedHours) && isDefined(adjustedHours) && adjustedHours > verifiedHours
      ? adjustedHours - verifiedHours
      : undefined;

  if (!isDefined(verifiedHours) || !isDefined(pendingHours)) {
    return {};
  }

  const pendingPaymentAmount = pendingHours * hourlyPayRate;

  return {
    paidOutAmountDisplay: isDefined(instantPayTotalAmount)
      ? formatDollarsAsUsd(instantPayTotalAmount)
      : undefined,
    pendingPaymentAmountDisplay:
      pendingPaymentAmount > 0 ? `+${formatDollarsAsUsd(pendingPaymentAmount)}` : undefined,
  };
}
